import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ReservationRequest,
  RequestStatus,
  PaginatedResponse,
} from "../types/request";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { createReservationRequestsApi } from "../api/reservationRequestsApi";
import { useCallback, useState, useMemo } from "react";

interface UseReservationRequestsOptions {
  hotelId: string;
  initialPage?: number;
  initialLimit?: number;
  initialStatus?: RequestStatus | RequestStatus[];
  initialFacilityType?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const useReservationRequests = ({
  hotelId,
  initialPage = 1,
  initialLimit = 10,
  initialStatus,
  initialFacilityType,
  sortBy,
  sortOrder = "desc",
}: UseReservationRequestsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();
  const { pagination, setPage, setLimit } = usePagination({
    initialPage,
    initialLimit,
  });

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Create API instance
  const api = useMemo(() => createReservationRequestsApi(callApi), [callApi]);

  // Query for fetching reservation requests
  const {
    data: requests,
    isLoading,
    error,
    refetch,
  } = useQuery<PaginatedResponse<ReservationRequest>, Error>({
    queryKey: [
      "reservationRequests",
      hotelId,
      pagination.page,
      pagination.limit,
      Array.isArray(initialStatus)
        ? [...initialStatus].sort().join(",")
        : initialStatus,
      initialFacilityType,
      searchTerm,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (initialStatus) {
        if (Array.isArray(initialStatus)) {
          params.status = initialStatus.join(",");
        } else {
          params.status = initialStatus;
        }
      }

      if (initialFacilityType) {
        params.facilityType = initialFacilityType;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      return await api.fetchReservationRequests(params);
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Mutation for updating request status
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      requestId,
      status,
      completedAt,
    }: {
      requestId: string;
      status: RequestStatus;
      completedAt?: string;
    }) => {
      return await api.updateReservationRequestStatus(requestId, {
        status,
        completedAt,
      });
    },
    onMutate: async ({ requestId, status, completedAt }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["reservationRequests", hotelId],
      });

      // Snapshot the previous value
      const previousRequests = queryClient.getQueryData([
        "reservationRequests",
        hotelId,
      ]);

      // Optimistically update the cache
      queryClient.setQueryData(
        ["reservationRequests", hotelId],
        (old: PaginatedResponse<ReservationRequest> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((request) =>
              request.requestId === requestId
                ? {
                    ...request,
                    status,
                    completedAt: completedAt || request.completedAt,
                  }
                : request
            ),
          };
        }
      );

      return { previousRequests };
    },
    onError: (_err, _newRequest, context) => {
      if (context?.previousRequests) {
        queryClient.setQueryData(
          ["reservationRequests"],
          context.previousRequests
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({
        queryKey: ["reservationRequests", hotelId],
      });
    },
  });

  // Get a specific reservation request
  const getReservationRequest = useCallback(
    async (requestId: string) => {
      return await api.fetchReservationRequestById(requestId);
    },
    [api]
  );

  return {
    requests,
    isLoading,
    error,
    updateRequestStatus: updateStatusMutation.mutateAsync,
    getReservationRequest,
    pagination: {
      page: pagination.page,
      limit: pagination.limit,
      totalCount: requests?.meta?.totalCount || 0,
      totalPages: requests?.meta?.totalPages || 0,
    },
    setPage,
    setLimit,
    setSearchTerm,
    refetch,
  };
};
